/**
 * CMS OASIS casting metadata from Roger-Docs `question_db.json` / `schema_data`.
 * Shape mirrors Roger-Docs / roger-emr backend `CMSCasting`.
 */

/** Visit-aware OASIS item ID entry (`{_used_by, _value}`) before schema-tag filtering. */
export interface CmsCastingItemIdByUsedBy {
  _used_by: string[];
  _value: string | null;
}

export interface CmsCasting {
  itm_id?: string | CmsCastingItemIdByUsedBy[] | null;
  val_id_mapping?: Record<string, string | null>;
  itm_id_mapping?: Record<string, string | null>;
}

/**
 * Leading OASIS/CMS item id before suffixes (`_…`) or trailing option letters.
 * `GG` + digits, or one section letter from `{A,B,C,D,J,K,M,N,O}` + digits —
 * Examples: `GG1800`, `M1700`, `C0100` from `M1700_COG_FUNCTION`.
 */
export const LETTERED_CMS_ITEM_ID_RE = /^((?:GG|[ABCDJKMNO])\d+)/;

/** CMS form item base from a full item id, e.g. `M1700_COG_FUNCTION` → `M1700`. */
export function parseLetteredCmsItemBase(itemId: string): string | null {
  return itemId.match(LETTERED_CMS_ITEM_ID_RE)?.[1] ?? null;
}

function addItmIdCandidates(
  candidates: string[],
  raw: CmsCasting['itm_id']
): void {
  if (typeof raw === 'string') {
    candidates.push(raw);
    return;
  }
  if (!Array.isArray(raw)) return;
  for (const entry of raw) {
    if (typeof entry._value === 'string') candidates.push(entry._value);
  }
}

/** Distinct lettered bases referenced by `itm_id` / `itm_id_mapping` on one casting block. */
export function letteredCmsItemBasesFromCasting(
  cms_casting: CmsCasting
): string[] {
  const candidates: string[] = [];
  if (cms_casting.itm_id !== undefined && cms_casting.itm_id !== null) {
    addItmIdCandidates(candidates, cms_casting.itm_id);
  }
  if (cms_casting.itm_id_mapping) {
    for (const value of Object.values(cms_casting.itm_id_mapping)) {
      if (typeof value === 'string') candidates.push(value);
    }
  }
  const bases = new Set<string>();
  for (const id of candidates) {
    const base = parseLetteredCmsItemBase(id);
    if (base) bases.add(base);
  }
  return [...bases].sort((a, b) => a.localeCompare(b));
}

/** Lettered CMS bases from `cms_casting` on one schema row. */
export function letteredCmsItemBasesFromQuestion(question: {
  cms_casting?: unknown;
}): string[] {
  const cms_casting = parseCmsCasting(question.cms_casting);
  if (!cms_casting) return [];
  return letteredCmsItemBasesFromCasting(cms_casting);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Roger-Docs `question_db` uses several `itm_id` shapes: usually a string, sometimes
 * `{_used_by, _value}[]` when the same qid maps to different OASIS items per schema
 * (e.g. GG0130A1 vs GG0130A4). Visit-filtered schema may collapse that to one string or null.
 */
function parseCmsCastingItemId(raw: unknown): CmsCasting['itm_id'] | undefined {
  if (raw === null) return null;
  if (typeof raw === 'string') return raw;
  if (!Array.isArray(raw)) return undefined;
  const items: CmsCastingItemIdByUsedBy[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const usedBy = entry._used_by;
    if (!Array.isArray(usedBy) || !usedBy.every(u => typeof u === 'string')) {
      continue;
    }
    const value = entry._value;
    items.push({
      _used_by: usedBy,
      _value: value === null || typeof value === 'string' ? value : null,
    });
  }
  return items.length > 0 ? items : undefined;
}

function parseStringRecord(
  raw: unknown
): Record<string, string | null> | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof key !== 'string') continue;
    if (value === null) {
      out[key] = null;
    } else if (typeof value === 'string') {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parse untrusted `cms_casting` from Roger-Docs `question_db` / `schema_data` rows.
 * Blocks may include `itm_id` (string or visit-aware array), `itm_id_mapping`, and/or
 * `val_id_mapping` — not always all three. Returns `undefined` if nothing usable remains
 * after normalizing `itm_id` ({@link parseCmsCastingItemId}).
 */
export function parseCmsCasting(raw: unknown): CmsCasting | undefined {
  if (!isRecord(raw)) return undefined;
  const itm_id = parseCmsCastingItemId(raw.itm_id);
  const val_id_mapping = parseStringRecord(raw.val_id_mapping);
  const itm_id_mapping = parseStringRecord(raw.itm_id_mapping);
  if (
    itm_id === undefined &&
    val_id_mapping === undefined &&
    itm_id_mapping === undefined
  ) {
    return undefined;
  }
  const out: CmsCasting = {};
  if (itm_id !== undefined) out.itm_id = itm_id;
  if (val_id_mapping) out.val_id_mapping = val_id_mapping;
  if (itm_id_mapping) out.itm_id_mapping = itm_id_mapping;
  return out;
}
